import { useState, useEffect } from 'react';
import portalApi from '../lib/api';
import { toast } from 'sonner';
import { Trash2, RotateCcw, Loader2, FileText, Shield, FileWarning } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const KIND = {
  case: { label: 'Case', icon: FileText, color: 'bg-sky-100 text-sky-700' },
  policy: { label: 'Policy', icon: Shield, color: 'bg-emerald-100 text-emerald-700' },
  document: { label: 'Document', icon: FileWarning, color: 'bg-amber-100 text-amber-700' },
};

export default function TrashPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await portalApi.getTrash()); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (it) => {
    setBusy(it.id);
    try { await portalApi.restoreTrash(it.id); toast.success('Restored'); await load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const purge = async (it) => {
    if (!window.confirm('Permanently delete this item? This cannot be undone.')) return;
    setBusy(it.id);
    try { await portalApi.purgeTrash(it.id); toast.success('Permanently deleted'); await load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Trash2 className="h-6 w-6 text-sky-500" /> Trash
        </h1>
        <p className="text-slate-500 text-sm">
          Recoverable deleted items. Restore a case to bring back its policies, documents and notes.
          Note: deleted document <em>files</em> cannot be recovered.
        </p>
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-sky-500" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center border-slate-200" data-testid="trash-empty">
          <Trash2 className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">Trash is empty. Deleted cases, policies and documents will appear here.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const meta = KIND[it.kind] || KIND.case;
            const Icon = meta.icon;
            return (
              <Card key={it.id} className="p-4 border-slate-200 flex flex-wrap items-center gap-3" data-testid={`trash-item-${it.entity_id}`}>
                <Badge className={`${meta.color} gap-1`}><Icon className="h-3 w-3" /> {meta.label}</Badge>
                <div className="flex-1 min-w-[220px]">
                  <div className="font-medium text-slate-900 text-sm">{it.summary || it.entity_id}</div>
                  <div className="text-xs text-slate-400">
                    Deleted {new Date(it.deleted_at).toLocaleString()} by {it.deleted_by_name}
                  </div>
                </div>
                <Button size="sm" variant="outline" disabled={busy === it.id} onClick={() => restore(it)} data-testid={`restore-${it.entity_id}`}>
                  {busy === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />} Restore
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  disabled={busy === it.id} onClick={() => purge(it)} data-testid={`purge-${it.entity_id}`}>
                  <Trash2 className="h-4 w-4 mr-1" /> Delete forever
                </Button>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
